import { useEffect, useMemo } from 'react';
import { useState } from 'react';
import { useDispatch } from 'react-redux';

import { ChatBubbleOvalLeftEllipsisIcon } from '@heroicons/react/24/outline';
import {
  DotsHorizontalIcon,
  Pencil1Icon,
  TrashIcon
} from '@radix-ui/react-icons';
import { EmptyState, Tooltip } from '@raystack/apsara';
import { DropdownMenu, Flex, IconButton, Text } from '@raystack/apsara';
import clsx from 'clsx';
import dayjs from 'dayjs';

import {
  DeleteChatModal,
  RenameChatModal
} from '@src/components/CoPilot/ChatOperations';
import { HistoryItemSkeleton } from '@src/components/CoPilot/Loaders';
import { HEADER_ICON_COLOR } from '@src/components/Custom/Header/Header';
import { useCopilotDispatch, useCopilotSelector } from '@src/contexts';
import { For, Show } from '@src/hoc';
import { useCopilot } from '@src/hooks/useCopilot';
import { ReactComponent as ChatIcon } from '@src/icons/ai-chat.svg';
import { setCopilotChatId } from '@src/redux/_reducers/appReducer';
import { stringifyJson } from '@src/utils';
import { MULTI_CHAT_STEPS } from '@src/utils/constants/COPILOT_CONSTANTS';
import { CopilotChat, HistoryListProps } from '@src/utils/types/CopilotTypes';
import { HistoryItemProps } from '@src/utils/types/CopilotTypes';

import styles from './History.module.css';

export const HistoryItem = ({
  historyItem,
  closePopover
}: HistoryItemProps) => {
  const { id = '', name = '' } = historyItem || {};
  const [hovered, setHovered] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const chatId = useCopilotSelector(state => state.chatId);

  const dispatch = useCopilotDispatch();
  const reduxDispatch = useDispatch();

  const actions = [
    {
      label: <Text size='small'>Rename</Text>,
      icon: <Pencil1Icon />,
      onclick: () => setShowEditModal(true)
    },
    {
      label: (
        <Text size='small' variant='danger'>
          Delete
        </Text>
      ),
      icon: <TrashIcon color='var(--rs-color-foreground-danger-primary)' />,
      onclick: () => setShowDeleteModal(true)
    }
  ];
  return (
    <>
      <Flex
        gap={2}
        align='center'
        justify='between'
        className={clsx(styles.historyItem, {
          [styles.hover]: hovered,
          [styles.active]: chatId === id
        })}
        data-test-id='chat-history-item-clicked'
        onClick={() => {
          dispatch({
            multiChatStep: MULTI_CHAT_STEPS.CHAT,
            chatId: id
          });
          reduxDispatch(setCopilotChatId(id));
          closePopover();
        }}
      >
        <Flex gap={2} align='center' justify='start'>
          <ChatIcon className={styles.icon} />
          <Tooltip message={name} side='bottom'>
            <Text size='mini' lineClamp={1} className={styles.name}>
              {name}
            </Text>
          </Tooltip>
        </Flex>

        <DropdownMenu
          placement='bottom-end'
          open={open}
          onOpenChange={val => {
            setHovered(val);
            setOpen(val);
          }}
        >
          <div className={styles.actionButton}>
            <DropdownMenu.Trigger asChild>
              <IconButton size={2} data-test-id={`chat-history-item-${id}`}>
                <DotsHorizontalIcon color={HEADER_ICON_COLOR} />
              </IconButton>
            </DropdownMenu.Trigger>
          </div>
          <DropdownMenu.Content>
            <DropdownMenu.Group>
              <For each={actions}>
                {(d, index) => (
                  <DropdownMenu.Item
                    key={index}
                    data-test-id={`chat-${d.label}`}
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      d.onclick();
                    }}
                    leadingIcon={d.icon}
                  >
                    {d.label}
                  </DropdownMenu.Item>
                )}
              </For>
            </DropdownMenu.Group>
          </DropdownMenu.Content>
        </DropdownMenu>
      </Flex>
      <Show when={showEditModal}>
        <RenameChatModal
          chatId={id}
          name={name}
          open={showEditModal}
          setOpen={setShowEditModal}
          closePopover={closePopover}
        />
      </Show>
      <Show when={showDeleteModal}>
        <DeleteChatModal
          chatId={id}
          open={showDeleteModal}
          setOpen={setShowDeleteModal}
          closePopover={closePopover}
        />
      </Show>
    </>
  );
};

export const HistoryList = ({ closePopover }: HistoryListProps) => {
  const { chatList: chats } = useCopilotSelector(state => state.historyAttrs);

  const { fetchChatList, loadingHistory, historyLoadingElementRef } =
    useCopilot();

  useEffect(() => {
    fetchChatList();
  }, [fetchChatList]);

  const chatsStr = stringifyJson(chats);

  const sortedChats = useMemo(() => {
    const recentChats: CopilotChat[] = [],
      previousChats: CopilotChat[] = [],
      olderChats: CopilotChat[] = [];

    chats.forEach((chat: CopilotChat) => {
      if (dayjs(chat.updated_at).isSame(dayjs(), 'day')) {
        recentChats.push(chat);
      } else if (dayjs(chat.updated_at).isSame(dayjs().add(-1, 'day'), 'day')) {
        previousChats.push(chat);
      } else {
        olderChats.push(chat);
      }
    });

    return [
      { name: 'Recent', chats: recentChats },
      { name: 'Previous', chats: previousChats },
      { name: 'Older', chats: olderChats }
    ].filter(d => d.chats.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatsStr]);

  return (
    <Show
      when={sortedChats?.length > 0 || loadingHistory}
      fallback={
        <EmptyState
          heading='No chat history yet'
          subHeading='Start the conversation to build your chat history'
          icon={<ChatBubbleOvalLeftEllipsisIcon />}
          classNames={{ container: styles.emptyStateContainer }}
        />
      }
    >
      <Flex direction='column' gap={8}>
        <For each={sortedChats}>
          {({ name, chats }, index) => (
            <Flex key={index} direction='column' gap={3}>
              <Text
                variant='tertiary'
                size='mini'
                weight='medium'
                style={{ paddingLeft: 'var(--rs-space-3)' }}
              >
                {name}
              </Text>
              <For each={chats}>
                {chat => (
                  <HistoryItem
                    key={chat.id}
                    historyItem={chat}
                    closePopover={closePopover}
                  />
                )}
              </For>
            </Flex>
          )}
        </For>
      </Flex>
      <div ref={historyLoadingElementRef}>
        <Show when={loadingHistory}>
          <HistoryItemSkeleton />
        </Show>
      </div>
    </Show>
  );
};
